import React, { useRef } from "react";
import { createPortal } from "react-dom";
import useModalA11y from "./useModalA11y";
import "./ui.scss";

/**
 * Small confirm / cancel modal. While `busy` the buttons are disabled and
 * Escape / overlay clicks are ignored so an in-flight request can't be
 * abandoned half-way.
 */
const ConfirmationDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Go back",
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);

  const requestClose = () => {
    if (!busy && onCancel) onCancel();
  };

  useModalA11y({
    open,
    onClose: requestClose,
    containerRef: dialogRef,
    initialFocusRef: cancelRef,
  });

  if (!open) return null;

  return createPortal(
    <div className="hr-dialog-root">
      <div
        className="hr-dialog-overlay"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className="hr-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="hr-dialog-title"
        aria-describedby="hr-dialog-message"
        tabIndex={-1}
      >
        <h2 id="hr-dialog-title">{title}</h2>
        <p id="hr-dialog-message">{message}</p>
        <div className="hr-dialog__actions">
          <button
            ref={cancelRef}
            type="button"
            className="hr-btn hr-btn--secondary"
            onClick={requestClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`hr-btn ${
              tone === "danger" ? "hr-btn--danger" : "hr-btn--primary"
            }`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmationDialog;
